import React, { useCallback, useState } from 'react'
import styled from 'styled-components'
import { media } from 'styled-bootstrap-grid'
import IntakeModal from './components/IntakeModal'
import ProgramFooter from './components/ProgramFooter'
import ProgramNav from './components/ProgramNav'
import TrustBanner from './components/TrustBanner'
import { ROUTES } from './content'
import { useJumpstartModal } from './jumpstartModal'
import { accent, body as bodyColor, hairline, H1, H2, ink, Inner, Lead, PageRoot, Pill, Section } from './styled'

const NAV_LINKS = [
  { href: ROUTES.accelerate, label: 'Accelerate' },
  { href: ROUTES.fullCertification, label: 'Full Certification' },
  { href: ROUTES.aboutMartin, label: 'About Martin' },
  { href: ROUTES.letter, label: 'Letter from the founder' },
]

const Hero = styled.header`
  max-width: 760px;
  margin: 0 auto;
  padding: 44px 16px 8px;

  ${media.sm`padding: 60px 20px 12px;`}
  ${media.lg`padding: 72px 24px 16px;`}
`

const Doc = styled.div`
  max-width: 760px;
  margin: 0 auto;
`

const Part = styled.div`
  & + & {
    margin-top: 44px;
    padding-top: 40px;
    border-top: 1px solid ${hairline};
  }
`

const PartHeading = styled(H2)`
  margin-bottom: 28px;
`

const Entry = styled.div`
  & + & {
    margin-top: 30px;
  }
`

const EntryHeading = styled.h3`
  font-size: 17.5px;
  font-weight: 700;
  line-height: 1.35;
  margin: 0 0 10px;
  color: ${ink};

  ${media.sm`font-size: 18.5px;`}
`

const Para = styled.p`
  font-size: 16px;
  line-height: 1.72;
  color: ${bodyColor};
  margin: 0 0 14px;

  &:last-child {
    margin-bottom: 0;
  }
`

const Bullets = styled.ul`
  margin: 4px 0 14px;
  padding: 0;
  list-style: none;
`

const Bullet = styled.li`
  position: relative;
  padding-left: 22px;
  font-size: 16px;
  line-height: 1.72;
  color: ${bodyColor};
  margin-bottom: 8px;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 11px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${accent};
  }
`

const renderEntry = (entry, index) => {
  const paragraphs = entry.paragraphs || entry.topParagraphs || []
  return (
    <Entry key={entry.heading || index}>
      {entry.heading && <EntryHeading>{entry.heading}</EntryHeading>}
      {paragraphs.map((text) => (
        <Para key={text.slice(0, 40)}>{text}</Para>
      ))}
      {entry.bullets && (
        <Bullets>
          {entry.bullets.map((item) => (
            <Bullet key={item}>{item}</Bullet>
          ))}
        </Bullets>
      )}
      {(entry.bottomParagraphs || []).map((text) => (
        <Para key={text.slice(0, 40)}>{text}</Para>
      ))}
    </Entry>
  )
}

/**
 * @param document  { eyebrow, title, intro, parts: [{ heading, entries }] }
 */
const LegalPage = ({ document: doc, onIntakeSubmit }) => {
  const jumpstartModalCopy = useJumpstartModal()
  const [modalOpen, setModalOpen] = useState(false)
  const openModal = useCallback(() => setModalOpen(true), [])
  const closeModal = useCallback(() => setModalOpen(false), [])

  return (
    <PageRoot>
      <TrustBanner />
      <ProgramNav links={NAV_LINKS} cta={{ label: 'Start Jumpstart' }} onCtaClick={openModal} />

      <Hero>
        <Pill>{doc.eyebrow}</Pill>
        <H1 $gap={doc.intro ? 20 : 8}>{doc.title}</H1>
        {doc.intro && <Lead $size="17px">{doc.intro}</Lead>}
      </Hero>

      <Section $tightTop>
        <Inner>
          <Doc>
            {doc.parts.map((part, index) => (
              <Part key={part.heading || index}>
                {part.heading && <PartHeading $max={28}>{part.heading}</PartHeading>}
                {part.entries.map(renderEntry)}
              </Part>
            ))}
          </Doc>
        </Inner>
      </Section>

      <ProgramFooter />

      <IntakeModal open={modalOpen} onClose={closeModal} onSubmit={onIntakeSubmit} {...jumpstartModalCopy} />
    </PageRoot>
  )
}

export default LegalPage
